import { useState, useCallback } from "react";
import { Component, ComponentType, PendingComponentSubAssembly } from "../../../../types/component";
import { ComponentMetadata } from "../../../../types/componentMetadataConfig";
import { normalizeOffWingTypeForForm } from "../../../../utils/offWingType";
import { mapComponentOwnershipFromApi } from "../../../../utils/componentOwnershipUtils";
import { buildLandingGearSerialFromSubAssemblies, clearOffWingFromComponentMetadata, landingGearHasSubAssembly } from "../../../../utils/landingGearAttachUtils";
import { validateSubAssemblyAttach } from "../../../../utils/subAssemblyUtils";
import { useComponentService } from "../../../../services/componentService";
import { OffWingType } from "../../../../types/DetachComponent";
import { useSnackbar } from "../../../../contexts/snackbar-context";

type ComponentForm = Partial<Component> & {
  hasSubAssembly?: boolean;
  pendingSubAssemblies?: PendingComponentSubAssembly[];
};

type ConfirmDetachSubAssemblyState = {
  open: boolean;
  step: "confirm" | "offWing";
  subAssemblyId: string | null;
  isPersisted: boolean;
  offWingType: OffWingType;
  shopVisitReasonId: string | null;
  shopVisitReasonLabel: string | null;
};

const defaultConfirmDetachSubAssembly: ConfirmDetachSubAssemblyState = {
  open: false,
  step: "confirm",
  subAssemblyId: null,
  isPersisted: false,
  offWingType: "In Shop",
  shopVisitReasonId: null,
  shopVisitReasonLabel: null,
};

export function useComponentForm() {
  const { getComponentSubAssemblies, detachSubAssemblyFromComponent } =
    useComponentService();
  const { showSuccess, showError } = useSnackbar();

  const buildDefaultCustomFields = useCallback((type: ComponentType) => {
    const fields = ComponentMetadata[type] || [];
    const metadata: Record<string, any> = {};
    fields.forEach((f) => {
      metadata[f.key] = "";
    });
    metadata.offWingType = "In Shop";
    return metadata;
  }, []);

  const buildDefaultForm = useCallback(
    (type: ComponentType): ComponentForm => ({
      componentType: type,
      type: type,
      manufacturer: "",
      model: "",
      serialNumber: "",
      dateManufactured: "",
      attachedToAirframe: "",
      attachmentLocation: "",
      ownershipStatus: null,
      operatorId: null,
      customMetadata: buildDefaultCustomFields(type),
      hasSubAssembly: false,
      pendingSubAssemblies: [],
    }),
    [buildDefaultCustomFields]
  );

  const [form, setForm] = useState<ComponentForm>(() =>
    buildDefaultForm("Engine")
  );
  const [selected, setSelected] = useState<Component | null>(null);
  const [isCreate, setIsCreate] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmDetachSubAssembly, setConfirmDetachSubAssembly] =
    useState<ConfirmDetachSubAssemblyState>(defaultConfirmDetachSubAssembly);
  const [isDetachingSubAssembly, setIsDetachingSubAssembly] = useState(false);
  const [formBaselineKey, setFormBaselineKey] = useState(0);

  const bumpFormBaseline = useCallback(
    () => setFormBaselineKey((key) => key + 1),
    []
  );

  const withLandingGearSerial = useCallback((formData: ComponentForm): ComponentForm => {
    if (formData.type !== "LandingGear" || !formData.hasSubAssembly) {
      return formData;
    }

    return {
      ...formData,
      serialNumber: buildLandingGearSerialFromSubAssemblies(
        formData.pendingSubAssemblies ?? []
      ),
    };
  }, []);

  const create = useCallback(() => {
    setSelected(null);
    setForm(buildDefaultForm("Engine"));
    setIsCreate(true);
    setOpen(true);
  }, [buildDefaultForm]);

  const edit = useCallback(async (component: Component) => {
    setSelected(component);

    let dateToUse = component.dateManufactured || "";
    if (dateToUse && dateToUse.includes("T")) {
      dateToUse = dateToUse.split("T")[0];
    }

    const rawMeta = component.customMetadata || {};
    const resolvedOffWing = normalizeOffWingTypeForForm(
      rawMeta.offWingType ??
        rawMeta.OffWingType ??
        rawMeta.OffWingStatus
    );

    const ownership = mapComponentOwnershipFromApi(component);

    const formData: ComponentForm = {
      ...component,
      type: component.type,
      dateManufactured: dateToUse,
      ownershipStatus:
        ownership.ownershipStatus === "" ? null : ownership.ownershipStatus,
      ownershipStatusLabel: ownership.ownershipStatusLabel,
      operatorId: ownership.operatorId,
      operatorName: ownership.operatorName,
      owner: ownership.owner,
      customMetadata: {
        ...rawMeta,
        offWingType: resolvedOffWing,
      },
      hasSubAssembly: landingGearHasSubAssembly(rawMeta),
      pendingSubAssemblies: [],
    };

    if (component.type === "LandingGear" && component.id) {
      try {
        const attached = await getComponentSubAssemblies(component.id);
        formData.hasSubAssembly =
          attached.length > 0 || landingGearHasSubAssembly(rawMeta);
        formData.pendingSubAssemblies = attached.map((item) => ({
          id: item.id,
          landingGearType: item.landingGearType,
          subAssemblyType: item.subAssemblyType,
          serialNumber: item.serialNumber,
          partNumber: item.partNumber,
          isPersisted: true,
        }));
      } catch {
        // Keep defaults if load fails; user can still edit other fields.
      }
    }

    if (formData.type === "LandingGear" && formData.hasSubAssembly) {
      formData.customMetadata = clearOffWingFromComponentMetadata(
        formData.customMetadata
      );
    }

    setForm(withLandingGearSerial(formData));
    setIsCreate(false);
    setOpen(true);
    bumpFormBaseline();
  }, [getComponentSubAssemblies, withLandingGearSerial, bumpFormBaseline]);

  const close = useCallback(() => setOpen(false), []);

  const change = useCallback(
    (key: keyof ComponentForm, value: any) => {
      setForm((prev) => {
        const updated: ComponentForm = { ...prev, [key]: value };
        if (key === "operatorId") {
          updated.operatorName = null;
        }
        if (key === "componentType" || key === "type") {
          const newType = value as ComponentType;
          updated.type = newType;
          updated.componentType = newType;
          const fields = ComponentMetadata[newType] || [];
          const metadata: Record<string, any> = {};
          fields.forEach((f) => {
            metadata[f.key] = prev.customMetadata?.[f.key] || "";
          });
          const prevOw =
            prev.customMetadata?.offWingType ??
            prev.customMetadata?.OffWingType;
          if (prevOw !== undefined && prevOw !== null && prevOw !== "") {
            metadata.offWingType = normalizeOffWingTypeForForm(String(prevOw));
          } else {
            metadata.offWingType = "In Shop";
          }
          updated.customMetadata = metadata;
          if (newType !== "LandingGear") {
            updated.hasSubAssembly = false;
            updated.pendingSubAssemblies = [];
          }
        }
        return updated;
      });
    },
    []
  );

  const changeCustomField = useCallback((key: string, value: any) => {
    setForm((prev) => {
      const updated: ComponentForm = {
        ...prev,
        customMetadata: {
          ...(prev.customMetadata || {}),
          [key]: value,
        },
      };
      if (key === "model") {
        updated.model = value;
      }
      return updated;
    });
  }, []);

  const setHasSubAssembly = useCallback((checked: boolean) => {
    setForm((prev) => {
      let customMetadata: Record<string, unknown> = {
        ...(prev.customMetadata || {}),
        lgHasSubAssembly: checked ? "Yes" : "No",
      };

      if (checked && prev.type === "LandingGear") {
        customMetadata = clearOffWingFromComponentMetadata(customMetadata);
      } else if (!checked && prev.type === "LandingGear") {
        const currentOffWing = String(customMetadata.offWingType ?? "").trim();
        if (!currentOffWing) {
          customMetadata = { ...customMetadata, offWingType: "In Shop" };
        }
      }

      const updated: ComponentForm = {
        ...prev,
        hasSubAssembly: checked,
        pendingSubAssemblies: checked ? prev.pendingSubAssemblies ?? [] : [],
        customMetadata,
        ...(!checked && !selected ? { serialNumber: "" } : {}),
      };

      return checked ? withLandingGearSerial(updated) : updated;
    });
  }, [selected, withLandingGearSerial]);

  const addPendingSubAssembly = useCallback((item: PendingComponentSubAssembly) => {
    setForm((prev) => {
      const existing = prev.pendingSubAssemblies ?? [];
      const validation = validateSubAssemblyAttach(existing, item);
      if (!validation.ok) {
        showError(validation.message);
        return prev;
      }

      return withLandingGearSerial({
        ...prev,
        pendingSubAssemblies: [...existing, { ...item, isPersisted: false }],
      });
    });
  }, [withLandingGearSerial, showError]);

  const removePendingSubAssembly = useCallback((subAssemblyId: string) => {
    setForm((prev) =>
      withLandingGearSerial({
        ...prev,
        pendingSubAssemblies: (prev.pendingSubAssemblies ?? []).filter(
          (item) => item.id !== subAssemblyId
        ),
      })
    );
  }, [withLandingGearSerial]);

  const requestRemovePendingSubAssembly = useCallback(
    (subAssemblyId: string, isPersisted = false) => {
      if (isCreate || !isPersisted) {
        removePendingSubAssembly(subAssemblyId);
        return;
      }

      setConfirmDetachSubAssembly({
        open: true,
        step: "confirm",
        subAssemblyId,
        isPersisted,
        offWingType: "In Shop",
        shopVisitReasonId: null,
        shopVisitReasonLabel: null,
      });
    },
    [isCreate, removePendingSubAssembly]
  );

  const handleConfirmDetachSubAssembly = useCallback(() => {
    setConfirmDetachSubAssembly((prev) => ({
      ...prev,
      step: "offWing",
      offWingType: prev.offWingType || "In Shop",
    }));
  }, []);

  const resetConfirmDetachSubAssembly = useCallback(() => {
    setConfirmDetachSubAssembly(defaultConfirmDetachSubAssembly);
  }, []);

  const handleSaveDetachSubAssembly = useCallback(async () => {
    const {
      subAssemblyId,
      isPersisted,
      offWingType,
      shopVisitReasonId,
    } = confirmDetachSubAssembly;

    if (!subAssemblyId) {
      showError("Select sub-assembly to detach");
      return;
    }

    if (!offWingType) {
      showError("Select off-wing type");
      return;
    }

    if (offWingType === "In Shop" && !shopVisitReasonId) {
      showError("Select in-shop reason");
      return;
    }

    setIsDetachingSubAssembly(true);
    try {
      if (isPersisted && selected?.id) {
        await detachSubAssemblyFromComponent(selected.id, subAssemblyId, {
          offWingType,
          shopVisitReasonId,
        });
      }

      removePendingSubAssembly(subAssemblyId);
      showSuccess("Sub-assembly detached");
      resetConfirmDetachSubAssembly();
      bumpFormBaseline();
    } catch {
      showError("Failed to detach sub-assembly");
      resetConfirmDetachSubAssembly();
    } finally {
      setIsDetachingSubAssembly(false);
    }
  }, [
    confirmDetachSubAssembly,
    selected?.id,
    detachSubAssemblyFromComponent,
    removePendingSubAssembly,
    resetConfirmDetachSubAssembly,
    showSuccess,
    showError,
    bumpFormBaseline,
  ]);

  return {
    form,
    formBaselineKey,
    selected,
    isCreate,
    open,
    create,
    edit,
    close,
    change,
    changeCustomField,
    setHasSubAssembly,
    addPendingSubAssembly,
    removePendingSubAssembly,
    requestRemovePendingSubAssembly,
    confirmDetachSubAssembly,
    setConfirmDetachSubAssembly,
    handleConfirmDetachSubAssembly,
    handleSaveDetachSubAssembly,
    resetConfirmDetachSubAssembly,
    isDetachingSubAssembly,
  };
}
